/**
 * מאזן — where CardCom sends the customer back. Never trusts the ?status=
 * query: it polls the server, which verifies the payment with CardCom itself
 * (webhook, or a direct GetLpResult if the webhook is late), then renders the
 * PDF. Status changes are announced (aria-live) for screen-reader users.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Clock, Download, XCircle } from 'lucide-react'
import { handleApiError } from '../../services/api'
import { mazanApi, type MazanStatus } from '../../features/mazan/model'

const POLL_MS = 3000
const MAX_POLLS = 100 // ~5 minutes

export default function MazanResultPage() {
  const { id = '' } = useParams()
  const [params] = useSearchParams()
  const returnedFailed = params.get('status') === 'failed'
  const [status, setStatus] = useState<MazanStatus | null>(null)
  const [emailed, setEmailed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const polls = useRef(0)

  useEffect(() => {
    let alive = true
    let timer: number | undefined
    const tick = async () => {
      try {
        const s = await mazanApi.status(id)
        if (!alive) return
        setStatus(s.status)
        setEmailed(s.emailed)
        const done = s.status === 'generated' || (s.status === 'draft') || (returnedFailed && s.status === 'pending_payment' && polls.current > 3)
        if (!done && ++polls.current < MAX_POLLS) timer = window.setTimeout(tick, POLL_MS)
      } catch (e) {
        if (alive) setError(handleApiError(e))
      }
    }
    tick()
    return () => { alive = false; window.clearTimeout(timer) }
  }, [id, returnedFailed])

  const download = async () => {
    setDownloading(true)
    try {
      await mazanApi.downloadPdf(id)
    } catch (e) {
      setError(handleApiError(e))
    } finally {
      setDownloading(false)
    }
  }

  let icon = <Clock size={40} className="text-amber-700" aria-hidden="true" />
  let title = 'מאמתים את התשלום…'
  let text = 'זה לוקח בדרך כלל כמה שניות. אין צורך לרענן את הדף.'
  if (status === 'paid') {
    title = 'התשלום התקבל — מפיקים את הדוח'
    text = 'הדוח נבנה כעת. הוא יופיע כאן ויישלח למייל בתוך דקה.'
  } else if (status === 'generated') {
    icon = <CheckCircle2 size={40} className="text-emerald-800" aria-hidden="true" />
    title = 'הדוח מוכן'
    text = emailed ? 'הדוח נשלח גם למייל שלך. חשבונית מס/קבלה נשלחת בנפרד מ־CardCom.' : 'אפשר להוריד את הדוח כאן. הוא נשמר גם באזור "הדוחות שלי".'
  } else if (status === 'failed') {
    title = 'התשלום התקבל, אך הפקת הדוח התעכבה'
    text = 'המשרד קיבל התראה ומטפל בכך. הדוח יישלח אליך למייל בהקדם. אין צורך לשלם שוב.'
  } else if (returnedFailed && status === 'pending_payment') {
    icon = <XCircle size={40} className="text-red-700" aria-hidden="true" />
    title = 'התשלום לא הושלם'
    text = 'לא חויבת. הנתונים שמורים — אפשר לחזור לסיכום ולנסות שוב.'
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-12">
      <div className="card text-center">
        <div className="flex justify-center">{icon}</div>
        <div role="status" aria-live="polite">
          <h1 className="mt-3 font-heading text-2xl font-bold text-gray-900">{title}</h1>
          <p className="mt-2 text-gray-800">{text}</p>
        </div>
        {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {status === 'generated' && (
            <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={download} disabled={downloading}>
              <Download size={18} aria-hidden="true" /> {downloading ? 'מוריד…' : 'הורדת הדוח (PDF)'}
            </button>
          )}
          {returnedFailed && status === 'pending_payment' && (
            <Link to={`/Mazan/report/${id}`} className="btn btn-primary">חזרה לסיכום</Link>
          )}
          <Link to="/Mazan/my" className="btn btn-secondary">הדוחות שלי</Link>
        </div>
      </div>
    </div>
  )
}
